import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery, gql, useMutation } from '@apollo/client';
import {
    Header, Button, Form, Input, TextArea,
    Divider, Label, Message, Loader, Container
} from 'semantic-ui-react';

import { MediaTile } from '../../../components/MediaTile';
import { useContentReviewerGate } from '../../../hooks/isContentReviewer';
import { PATHS } from '../../../paths';
import { parseApiDate } from '../../../utils/date';
import { POST_TITLE_LIMIT, POST_DESCRIPTION_LIMIT, characterCountColour } from '../../../constants';
import { StreamBadge, STREAM_BADGE_FIELDS } from '../../../components/StreamBadge';

// Post is a union (CommunityUpdate | CommunityPrayerRequest | LeadershipUpdate
// | LeadershipPrayerRequest | Birthday); fields must be selected per member.
const POST_SENT_FIELDS = `
    id
    title
    description
    type
    publishAt
    ${STREAM_BADGE_FIELDS}
    images { id url contentType durationSeconds }
`;

const GET_SENT_POST = gql`
     query SentPost($postId: Int!) {
         sentPost(id: $postId) {
             ... on CommunityUpdate { ${POST_SENT_FIELDS} }
             ... on CommunityPrayerRequest { ${POST_SENT_FIELDS} }
             ... on LeadershipUpdate { ${POST_SENT_FIELDS} }
             ... on LeadershipPrayerRequest { ${POST_SENT_FIELDS} }
             ... on Birthday { ${POST_SENT_FIELDS} }
         }
     }
`;

const UPDATE_SENT_POST = gql`
    mutation UpdateSentPost($id: Int!, $title: String!, $description: String!) {
        updateSentPost(id: $id, title: $title, description: $description) {
            ... on CommunityUpdate { id }
            ... on CommunityPrayerRequest { id }
            ... on LeadershipUpdate { id }
            ... on LeadershipPrayerRequest { id }
            ... on Birthday { id }
        }
    }
`;

type EditableFields = { title: string; description: string };

// --- Component ---

const PostSentView: React.FC = () => {
    const { postId } = useParams<{ postId: string }>();
    const navigate = useNavigate();
    const { loading: meLoading, isContentReviewer } = useContentReviewerGate();
    const [isExiting, setIsExiting] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    const { data, loading, error } = useQuery(GET_SENT_POST, {
        variables: { postId: Number(postId) },
        skip: !postId,
        // Editability is time-window-based, so a cached "not editable" from a previous visit
        // must not linger — always re-check against the server.
        fetchPolicy: 'network-only',
    });

    // Seeded once from the query result, then edited locally until Save.
    const [form, setForm] = useState<EditableFields | null>(null);
    useEffect(() => {
        if (data?.sentPost && form == null) {
            setForm({
                title: data.sentPost.title || '',
                description: data.sentPost.description || '',
            });
        }
    }, [data, form]);

    const [updatePost, { loading: saving }] = useMutation(UPDATE_SENT_POST);

    if (!meLoading && !isContentReviewer) {
        return <Navigate to={PATHS.CLASSES} replace />;
    }

    const handleSave = async () => {
        if (!form) return;
        setActionError(null);
        try {
            await updatePost({
                variables: {
                    id: Number(postId),
                    title: form.title,
                    description: form.description,
                }
            });
            setIsExiting(true);
            setTimeout(() => navigate(PATHS.POSTS_SCHEDULED), 500);
        } catch (err: any) {
            setActionError(err?.message || 'Could not save changes.');
        }
    };

    // Gated on `loading` alone — `form` never gets set when `sentPost` legitimately resolves
    // to null, so including it here would spin this loader forever instead of ever reaching
    // the messages below.
    if (loading) return <Loader active inline='centered' style={{ marginTop: '5em' }}>Loading post...</Loader>;

    if (error) {
        return (
            <Container text style={{ padding: '3em 0em' }}>
                <Message error content={error.message || 'Could not load this post.'} />
            </Container>
        );
    }

    // A missing result covers both "doesn't exist" and "past the edit window" — the resolver
    // treats those the same way (see backend `sentPost`), so a plain message covers both.
    if (!data?.sentPost) {
        return (
            <Container text style={{ padding: '3em 0em' }}>
                <Message error content="This post can no longer be edited (it may be more than 2 days old)." />
            </Container>
        );
    }

    // `form` is seeded from `data.sentPost` by the effect above, one render after data lands.
    if (!form) return <Loader active inline='centered' style={{ marginTop: '5em' }}>Loading post...</Loader>;

    const { sentPost: post } = data;
    const publishDate = parseApiDate(post.publishAt);
    const overLimit = form!.title.length > POST_TITLE_LIMIT || form!.description.length > POST_DESCRIPTION_LIMIT;

    return (
        <Container
            text
            style={{
                padding: '3em 0em',
                transition: 'opacity 0.5s ease-in-out',
                opacity: isExiting ? 0 : 1
            }}
        >
            <Label basic color="grey" size="small" style={{ marginBottom: '1em' }}>{post.type}</Label>
            <div style={{ marginBottom: '10px' }}>
                <StreamBadge stream={post.stream} size="small" />
            </div>
            <Header as="h1" style={{ marginTop: 0 }}>Edit sent post</Header>
            <p style={{ color: '#666' }}>
                Published {publishDate?.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}.
                Title and description can be corrected for up to 2 days after publishing.
            </p>

            {actionError && <Message error content={actionError} />}

            {post.images.length > 0 && (
                <div style={{ display: 'flex', overflowX: 'auto', gap: '12px', paddingBottom: '15px', marginBottom: '2em' }}>
                    {post.images.map((img: any) => (
                        <MediaTile
                            key={img.id}
                            url={img.url}
                            contentType={img.contentType}
                            durationSeconds={img.durationSeconds}
                            rounded
                            bordered
                            controls
                            style={{ height: '180px', objectFit: 'cover' }}
                        />
                    ))}
                </div>
            )}

            <Form>
                <Form.Field required>
                    <label>Title</label>
                    <Input
                        value={form!.title}
                        disabled={saving}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, title: value }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form!.title.length, POST_TITLE_LIMIT) }}>
                        {form!.title.length} / {POST_TITLE_LIMIT}
                    </div>
                </Form.Field>

                <Form.Field required>
                    <label>Description</label>
                    <TextArea
                        rows={6}
                        value={form!.description}
                        disabled={saving}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, description: value as string }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form!.description.length, POST_DESCRIPTION_LIMIT) }}>
                        {form!.description.length} / {POST_DESCRIPTION_LIMIT}
                    </div>
                </Form.Field>
            </Form>

            <Divider style={{ margin: '3em 0 2em 0' }} />

            <Button
                positive
                size="huge"
                fluid
                icon="checkmark"
                labelPosition="left"
                content="Save changes"
                loading={saving}
                disabled={isExiting || saving || !form!.title || !form!.description || overLimit}
                onClick={handleSave}
            />
        </Container>
    );
};

export { PostSentView };
